import React, { useEffect, useState } from "react";
import BreadCrumb from "src/Components/GlobalComponents/BreadCrumb";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import TableWrapper from "src/Components/GlobalComponents/TableWrapper";
import { IHomecareService, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import editIcon from "src/assets/images/edit-icon.png";
import deleteIcon from "src/assets/images/delete-icon.png";
import UpsertHomecareServices from "src/Components/AdminComponents/UpsertHomecareServices";
import httpRequest from "src/helpers/httpRequest";
import qs from "qs";
import { toast } from "react-toastify";
import ToggleRadioBtn from "src/Components/ToggleRadioBtn";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
import { CURRENCY } from "src/lib/constans";
interface IResponse {
  documents: IHomecareService[];
  paginated: IPaginated;
}

const HomecareServices: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "homeServiceManagement", false);

  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [isEdit, setIsEdit] = useState<string | null>(null);
  const [isUpsert, setIsUpsert] = useState<boolean>(false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [allServices, setAllServices] = useState<IHomecareService[]>([]);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  });
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    search: "",
    bool: false,
  });

  const handleChangeText = (txt: string) => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: txt,
    }));
  };

  const handleClose = () => {
    setIsEdit(null);
    setIsUpsert((prev) => !prev);
  };

  const deleteModalClose = () => {
    setDeleteModal(false);
    setDeleteId("");
  };

  const deleteRequests = async () => {
    setDeleteLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "delete",
      path: `/home-care-services/delete/${deleteId}`,
      params: {},
    });
    if (res) {
      toast(res?.message || "Success!", { type: "success" });
      setQueryParams((prev) => ({
        ...prev,
        bool: !prev.bool,
        page:
          allServices?.length > 1
            ? prev?.page
            : prev?.page > 1
            ? prev?.page - 1
            : prev?.page,
      }));
      setDeleteModal(false);
      setDeleteId("");
    } else {
      toast(err, { type: "error" });
    }
    setDeleteLoader(false);
  };

  const handleEdit = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setIsUpsert(true);
    setIsEdit(id);
  };

  const handleDelete = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setDeleteModal(true);
    setDeleteId(id);
  };

  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  const handleUpdateList = () => {
    setQueryParams((prev) => ({
      ...prev,
      bool: !prev.bool,
    }));
    setIsUpsert(false);
  };

  const printCategory = (val: string[]): JSX.Element[] | string => {
    return val
      ? val.map((v, i) => (
          <span key={v}>
            <span>{v}</span>
            {i + 1 !== val.length && <span className="mr-0.5">,</span>}
          </span>
        ))
      : "";
  };

  const [statusLoader, setStatusLoader] = useState<string | null>(null);
  const handleStatusToogle = async (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setStatusLoader(id);
    const { res, err } = await httpRequest<{
      message: string;
      status: boolean;
    }>({
      method: "put",
      path: `/home-care-services/updateStatus/${id}`,
      params: {},
    });
    if (res) {
      const { message = "Success!", status = false } = res;
      toast(message, { type: "success" });
      setAllServices((prev) =>
        prev.map((v) => (v._id === id ? { ...v, status } : v))
      );
    } else {
      toast(err, { type: "error" });
    }
    setStatusLoader(null);
  };

  useEffect(() => {
    (async () => {
      setIsLoader(true);
      let { bool, ...remainingParams } = queryParams;
      const { res, err } = await httpRequest<IResponse>({
        path: `/home-care-services/getAll?${qs.stringify(remainingParams)}`,
      });
      if (res) {
        setAllServices(res?.documents);
        setPaginated(res?.paginated);
      } else {
        toast(err, { type: "error" });
      }
      setIsLoader(false);
    })();
  }, [queryParams]);

  return (
    <>
      <ConfirmationModal
        show={deleteModal}
        onClose={deleteModalClose}
        title="Are you sure you want to delete?"
        leftBtnText="No"
        rightBtnText="Yes"
        onConfirm={deleteRequests}
        loader={deleteLoader}
      />
      <BreadCrumb
        current="At-home Services"
        previous="dashboard"
        navigateUrl="/admin"
        btnText={"Add New Service"}
        btnShow={true}
        onPress={() => {
          if (!hasWriteAccess) {
            return;
          }

          handleClose();
        }}
        isUpsert={isUpsert}
        btnClassName={`${!hasWriteAccess ? "cursor-not-allowed" : ""}`}
        BtnId="add-service-btn"
      />
      <div className="table-container">
        {isUpsert ? (
          <UpsertHomecareServices
            onSuccess={handleUpdateList}
            isEdit={isEdit}
          />
        ) : (
          <>
            <SearchFilterInput onChangeText={handleChangeText} />
            <TableWrapper
              isLoading={isLoader}
              thead={[
                "S.No",
                "Service Name",
                "Category",
                "Price",
                "Status",
                "Action",
              ]}
              page={paginated?.currentPage || 1}
              total={paginated?.totalPages || 0}
              columnWidth="10% 30% 20% 15% 15% 10%"
              onChange={handlePageChange}
            >
              <div className="tbody-content-height">
                {allServices?.map((item: IHomecareService, index: number) => (
                  <ul
                    key={index}
                    className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                    style={{ gridTemplateColumns: "10% 30% 20% 15% 15% 10%" }}
                  >
                    <li>
                      {(paginated?.currentPage - 1) * queryParams.limit +
                        (index + 1)}
                    </li>
                    <li>{item?.serviceName || ""}</li>
                    <li>
                      {printCategory(
                        item.categories.map(
                          (v: { _id: string; name: string }) => v.name
                        )
                      )}
                    </li>
                    <li>
                      {CURRENCY} {item?.price || "-"}
                    </li>
                    <li
                      className="justify-center"
                      style={{ justifyContent: "center", paddingLeft: 0 }}
                    >
                      <div
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                      >
                        <ToggleRadioBtn
                          isToggled={item.status}
                          handleToggle={() => handleStatusToogle(item._id)}
                          loader={statusLoader === item._id || false}
                          id={`toggle-radio-btn-${index + 1}`}
                          disabled={!hasWriteAccess}
                        />
                      </div>
                    </li>
                    <li className="action-last-colum">
                      <img
                        id={`edit-icon-${index + 1}`}
                        onClick={() => handleEdit(item?._id)}
                        src={editIcon}
                        alt=""
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                      />
                      <img
                        id={`delete-icon-${index + 1}`}
                        onClick={() => handleDelete(item?._id)}
                        src={deleteIcon}
                        alt=""
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                      />
                    </li>
                  </ul>
                ))}
              </div>
            </TableWrapper>
          </>
        )}
      </div>
    </>
  );
};

export default HomecareServices;
